import type { ReactNode } from "react";
import { LayoutGrid, List, Loader2, Search } from "lucide-react";
import { cn } from "@/lib/cn";
import { DENTIST_STATUS_META, DENTIST_STATUS_ORDER, type DentistStatusBucket } from "./dentist-status";
import { OrdenarPedidos } from "./OrdenarPedidos";
import type { OrderSortField, SortDirection } from "./prosthesis-orders-sort";

type Props = {
  busca: string;
  onBusca: (v: string) => void;
  status: {
    selecionados: DentistStatusBucket[];
    contagem: Record<DentistStatusBucket, number>;
    total: number;
    onLimpar: () => void;
    /** Os chips do desktop filtram por um status de cada vez. */
    onSomente: (s: DentistStatusBucket) => void;
  };
  ordem: { campo: OrderSortField; direcao: SortDirection; onChange: (c: OrderSortField, d: SortDirection) => void };
  visualizacao: "card" | "list";
  onVisualizacao: (v: "card" | "list") => void;
  carregando: boolean;
  /** Ao lado da busca — o filtro de organizações no acesso pessoal. */
  extra?: ReactNode;
};

/** Filtros da lista de pedidos no desktop: busca, chips de status, Ordenar e Cards/Lista. */
export function BarraDePedidosDesktop({ busca, onBusca, status, ordem, visualizacao, onVisualizacao, carregando, extra }: Props) {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-1 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative w-full min-w-0 sm:max-w-md sm:flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <input
            type="search"
            value={busca}
            onChange={(e) => onBusca(e.target.value)}
            placeholder="Buscar paciente, lab, material..."
            className="h-11 w-full rounded-md border border-zinc-200 bg-white pl-9 pr-3 text-base text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 sm:h-9 sm:text-sm dark:border-border dark:bg-card dark:text-foreground"
          />
        </div>
        {extra}

        <div
          data-tour="protese-status-filtro"
          /* Faixa rolável de uma linha no celular: com `flex-wrap`, cinco filtros de larguras
             diferentes formavam linhas irregulares e pareciam texto solto. A rolagem é do
             componente, não da página. */
          className="hidden snap-x items-center gap-1 overflow-x-auto scroll-px-1 rounded-lg border border-zinc-200 bg-zinc-50 p-1 scrollbar-hide md:inline-flex md:flex-wrap md:overflow-visible dark:border-border dark:bg-muted/40">
          <StatusFilterButton
            label="Todos"
            count={status.total}
            active={status.selecionados.length === 0}
            onClick={status.onLimpar}
          />
          {DENTIST_STATUS_ORDER.map((s) => (
            <StatusFilterButton
              key={s}
              label={DENTIST_STATUS_META[s].label}
              count={status.contagem[s]}
              active={status.selecionados.includes(s)}
              onClick={() => status.onSomente(s)}
            />
          ))}
        </div>

        <OrdenarPedidos campo={ordem.campo} direcao={ordem.direcao} onChange={ordem.onChange} />

        {carregando && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}
      </div>

      <div data-tour="protese-visualizacao" className="flex h-12 w-full items-center rounded-md border border-zinc-200 bg-white p-0.5 sm:inline-flex sm:h-9 sm:w-auto dark:border-border dark:bg-card">
        <button
          type="button"
          onClick={() => onVisualizacao("card")}
          aria-label="Visualizar em cards"
          className={cn(
            "inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded px-3 text-xs font-medium transition-colors sm:h-8 sm:flex-none",
            visualizacao === "card"
              ? "bg-brand-50 font-semibold text-brand-700 ring-1 ring-brand-200 dark:bg-primary/15 dark:text-primary dark:ring-primary/30"
              : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
          )}
        >
          <LayoutGrid className="h-3.5 w-3.5" />
          Cards
        </button>
        <button
          type="button"
          onClick={() => onVisualizacao("list")}
          aria-label="Visualizar em lista"
          className={cn(
            "inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded px-3 text-xs font-medium transition-colors sm:h-8 sm:flex-none",
            visualizacao === "list"
              ? "bg-brand-50 font-semibold text-brand-700 ring-1 ring-brand-200 dark:bg-primary/15 dark:text-primary dark:ring-primary/30"
              : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
          )}
        >
          <List className="h-3.5 w-3.5" />
          Lista
        </button>
      </div>
    </div>
  );
}

function StatusFilterButton({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex h-11 shrink-0 snap-start items-center gap-1.5 rounded-md px-2.5 text-[12.5px] font-semibold transition-colors sm:h-8",
        active
          ? "bg-brand-50 text-brand-700 ring-1 ring-brand-200 dark:bg-primary/15 dark:text-primary dark:ring-primary/30"
          : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
      )}
    >
      {label}
      <span
        className={cn(
          "inline-flex h-4 min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-bold tabular-nums",
          active ? "bg-brand-100 text-brand-700 dark:bg-primary/25 dark:text-primary" : "bg-zinc-200 text-zinc-600 dark:bg-muted dark:text-muted-foreground",
        )}
      >
        {count}
      </span>
    </button>
  );
}
